import { t as toggleBooleanAttribute } from './toggleBooleanAttribute-Cp5m6OeZ.js';

/** Maps expand-from attribute values to CSS breakpoint custom properties */
const EXPAND_FROM_BREAKPOINTS = {
  sm: '--breakpoint-sm',
  md: '--breakpoint-md',
  lg: '--breakpoint-lg',
  xl: '--breakpoint-xl',
};

class CollapsibleElement extends HTMLElement {
  /** @type {HTMLElement} */ #controller = this.querySelector('[aria-controls]');
  /** @type {HTMLElement} */ #target = this.#controller.ariaControlsElements[0];
  /** @type {boolean} */ #persist = this.hasAttribute('persist');
  /** @type {MediaQueryList | null} */ #expandFromMq = null;
  /** @type {boolean} */ #lockedOpen = false;

  connectedCallback() {
    if (this.#persist) this.#restoreState();
    this.#controller.addEventListener('click', this.#onClick);
    this.#setupExpandFrom();
  }

  disconnectedCallback() {
    this.#controller.removeEventListener('click', this.#onClick);
    this.#expandFromMq?.removeEventListener('change', this.#onExpandFromChange);
  }

  #onClick = () => {
    if (this.#lockedOpen) return;
    this.#handleClick();
  };

  #handleClick() {
    toggleBooleanAttribute(this.#controller, 'aria-expanded');
    toggleBooleanAttribute(this.#target, 'aria-hidden');
    if (this.#persist) this.#saveState();
  }

  #setupExpandFrom() {
    const expandFrom = this.getAttribute('expand-from');
    const cssVar = EXPAND_FROM_BREAKPOINTS[expandFrom];
    if (!cssVar) return;

    const value = getComputedStyle(document.documentElement).getPropertyValue(cssVar).trim();
    if (!value) return;

    this.#expandFromMq = window.matchMedia(`(width >= ${value})`);
    this.#expandFromMq.addEventListener('change', this.#onExpandFromChange);
    this.#onExpandFromChange();
  }

  #onExpandFromChange = () => {
    this.#lockedOpen = Boolean(this.#expandFromMq?.matches);
    this.#setExpanded(this.#lockedOpen);
  };

  /** @param {boolean} expanded */
  #setExpanded(expanded) {
    this.#controller.setAttribute('aria-expanded', String(expanded));
    this.#target.setAttribute('aria-hidden', String(!expanded));
  }

  #storageKey() {
    return `collapsible:${this.#target.id}`;
  }

  #saveState() {
    const expanded = this.#controller.getAttribute('aria-expanded') === 'true';
    sessionStorage.setItem(this.#storageKey(), expanded);
  }

  #restoreState() {
    const stored = sessionStorage.getItem(this.#storageKey());
    if (stored === null) return;

    this.#setExpanded(stored === 'true');
  }
}

customElements.define('collapsible-element', CollapsibleElement);
